import React, { useMemo, useCallback } from 'react';
import { BarCustomLayerProps, BarDatum, ComputedDatum } from '@nivo/bar';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { CHART_COLORS } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/ChartColors';
import { S2GroupedBarChart } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { SingleMetricTooltipContent } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GenericTableViewTooltip';
import { BarTotalPopoverLayer } from 'apps/sigma2-ui/src/components/BarTotalPopoverLayer';
import { DefaultOverallDisplayMode } from 'libs/end-to-end/src/view-configurations';
import { GroupedBarChartPlaceholder } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GroupedBarChartPlaceholder';
import { computeValue } from 'apps/sigma2-ui/src/explore-section/utils/TableViewUtils';
import { computeUnits } from 'apps/sigma2-ui/src/explore-section/utils/TableViewUtils';

// Custom layer to draw a line at the bottom of the chart
export const BottomLineLayer = ({
  innerHeight,
  innerWidth,
}: BarCustomLayerProps<BarDatum>) => (
  <line
    x1={0}
    y1={innerHeight}
    x2={innerWidth}
    y2={innerHeight}
    stroke="#b8babc"
    strokeWidth={1}
  />
);

/**
 * Grouped bar chart component - displays multiple horizontal bar charts in a grid.
 *
 * Features:
 * - One horizontal bar chart per metric
 * - Each chart shows categories broken down by groupBy dimension
 * - 2-column grid layout
 * - Colors represent different categories (matching pie/stacked charts)
 * - Shared legend showing all categories
 * - Smart formatting and tooltips
 *
 * @example
 * ```tsx
 * <GroupedBarChartMode
 *   data={tableData}
 *   visibleRows={visibleRows}
 * />
 * ```
 */
export const GroupedBarChartMode: React.FC<TableViewModeArguments> = ({
  data,
  visibleRows,
  viewConfiguration,
}) => {
  const { getColumnDisplayName } = useGetColumnDisplayName();
  const language = useLanguage();

  // Create one chart per metric (similar to stacked bar chart pattern)
  const metricCharts = useMemo(() => {
    const categoryKeys: string[] = [];
    const categoryIndices: number[] = [];

    // Build list of categories (filter by visibleRows)
    data.rows.forEach((row, rowIndex) => {
      if (visibleRows.length === 0 || visibleRows.includes(rowIndex)) {
        categoryKeys.push(row[0]); // First column is the category (groupBy dimension)
        categoryIndices.push(rowIndex);
      }
    });

    // Create a separate chart for each metric
    const charts = data.columnHeaders.map((metricName, metricIndex) => {
      // Build data for this metric - one bar per category
      const chartData = categoryKeys.map((categoryName, categoryIdx) => {
        const rowIndex = categoryIndices[categoryIdx];
        const rawValue = data.rows[rowIndex][metricIndex + 1];
        const numericValue = safeParseFloat(rawValue) || 0;

        return {
          category: categoryName,
          value: numericValue,
          rawValue: rawValue,
          sampleSize: data.sampleSizes[rowIndex][metricIndex + 1],
          numeratorSize: data.numeratorSizes[rowIndex]?.[metricIndex + 1] ?? 0,
        };
      });

      return {
        metricName,
        data: chartData,
      };
    });

    return { charts, categoryKeys };
  }, [
    data.columnHeaders,
    data.rows,
    data.sampleSizes,
    data.numeratorSizes,
    visibleRows,
  ]);

  // Generate colors for each category (matching pie/stacked bar)
  const categoryColors = useMemo(() => {
    return metricCharts.categoryKeys.map((_, index) => {
      return CHART_COLORS[index % CHART_COLORS.length];
    });
  }, [metricCharts.categoryKeys]);

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const colorsCb = useCallback(
    (bar: ComputedDatum<BarDatum>) => {
      const categoryIndex = metricCharts.categoryKeys.indexOf(
        bar.indexValue as string,
      );
      return categoryColors[categoryIndex] || categoryColors[0];
    },
    [metricCharts.categoryKeys, categoryColors],
  );

  // Render a single metric's horizontal bar chart
  const renderChart = useCallback(
    (
      chartData: Array<{
        category: string;
        value: number;
        rawValue: string;
        sampleSize: number;
        numeratorSize: number;
      }>,
      metricName: string,
    ) => {
      // Calculate height: 15px per bar + 8px gap between bars + margins
      const barHeight = 15;
      const barGap = 8;
      const numBars = chartData.length;
      const totalBarsHeight = numBars * barHeight + (numBars - 1) * barGap;
      const chartHeight = Math.max(200, totalBarsHeight + 100);

      const metricInfo = getMetricAndFocusAreaFromColumnName({
        columnName: metricName,
      });

      if (!metricInfo) {
        return null;
      }

      const metricUnits = computeUnits({
        metricInfo,
        viewConfiguration,
      });

      const units =
        viewConfiguration.overallDisplayMode === 'count'
          ? 'count'
          : metricUnits;

      const xGroups = chartData.map((data) => ({
        id: data.category,
        label: data.category,
        groupLevels: [
          {
            id: metricName,
            value: computeValue({
              value: data.value,
              numeratorSize: data.numeratorSize,
              chartUnits: units,
              overallDisplayMode: viewConfiguration.overallDisplayMode,
            }),
            tooltip: (
              <SingleMetricTooltipContent
                metricColumn={metricName}
                percentageValue={data.value}
                levelDisplayName={data.category}
                totalSubjects={data.sampleSize}
                numeratorSize={data.numeratorSize}
                units={metricUnits}
                overallDisplayMode={viewConfiguration.overallDisplayMode}
              />
            ),
          },
        ],
      }));

      // if metric is not boolean and overall display mode is count, show placeholder instead
      if (
        viewConfiguration.overallDisplayMode === 'count' &&
        !metricInfo.metric.isMetricBoolean
      ) {
        return (
          <GroupedBarChartPlaceholder
            key={metricName}
            metricName={metricName}
            chartHeight={chartHeight}
          />
        );
      }

      return (
        <div key={metricName} className="w-full">
          {/* Metric title */}
          <h3 className="text-base font-semibold text-gray-800 mb-3 px-2">
            {getColumnDisplayName(metricName)}
          </h3>

          <div
            className="w-full bg-white"
            style={{ height: `${chartHeight}px` }}
          >
            <S2GroupedBarChart
              xGroups={xGroups}
              xLevels={[
                {
                  id: metricName,
                  label: metricName,
                },
              ]}
              colorsCb={colorsCb}
              units={units}
              yAxisLabel=""
              verticalGroupPaddingAsFactorOfHeight={0.3}
              horizontalMinBarWidth={Infinity}
              verticalBarFixedThickness={true}
              verticalMinHeight={230}
              verticalMaxHeight={400}
              hoverEffect={false}
              margin={{ top: 10, right: 50, bottom: 50, left: 150 }}
              enableGridX={false}
              enableGridY={true}
              markers={[
                {
                  axis: 'x',
                  value: 0,
                  lineStyle: {
                    stroke: '#b8babc',
                    strokeWidth: '1px',
                  },
                },
              ]}
              layers={[BottomLineLayer, BarTotalPopoverLayer]}
              axisBottom={{
                tickSize: 4,
                tickPadding: 6,
              }}
            />
          </div>
        </div>
      );
    },
    [
      getMetricAndFocusAreaFromColumnName,
      viewConfiguration,
      getColumnDisplayName,
      colorsCb,
    ],
  );

  if (metricCharts.charts.length === 0) {
    return (
      <div className="w-full bg-white py-4">
        <div className="w-full h-[400px] flex items-center justify-center text-gray-500">
          {language.noDataAvailable()}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white py-4">
      {/* Shared legend for all categories */}
      <div className="w-full mb-8 px-4">
        <div className="flex flex-wrap items-center gap-6">
          {metricCharts.categoryKeys.map((categoryKey, index) => (
            <div key={categoryKey} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: categoryColors[index] }}
              />
              <span className="text-xs text-gray-600">{categoryKey}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Chart grid - one chart per metric */}
      <div className="w-full grid grid-cols-2 gap-6">
        {metricCharts.charts.map((chart) =>
          renderChart(chart.data, chart.metricName),
        )}
      </div>
    </div>
  );
};
